
import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trash2, ExternalLink, MapPin, Globe } from "lucide-react";
import { Client } from "./types";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ClientsTableProps {
  clients: Client[];
  onClientClick: (clientId: string) => void;
  onClientUpdate: () => void;
}

export const ClientsTable = ({ clients, onClientClick, onClientUpdate }: ClientsTableProps) => {
  const [deletingClient, setDeletingClient] = useState<Client | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (client: Client, event: React.MouseEvent) => {
    event.stopPropagation();
    setDeletingClient(client);
  };

  const confirmDelete = async () => {
    if (!deletingClient) return;

    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', deletingClient.id);

      if (error) {
        console.error('Delete error:', error);
        
        // Provide more specific error messages
        if (error.message.includes('row-level security')) {
          toast.error(`Cannot delete "${deletingClient.name}": You don't have permission to delete this client. This may be orphaned data that requires admin cleanup.`);
        } else {
          toast.error(`Failed to delete "${deletingClient.name}": ${error.message}`);
        }
      } else {
        toast.success(`Client "${deletingClient.name}" deleted successfully`);
        onClientUpdate();
      }
    } catch (error) {
      console.error('Unexpected error during deletion:', error);
      toast.error(`Unexpected error deleting "${deletingClient.name}". Please try again or contact support.`);
    } finally {
      setIsDeleting(false);
      setDeletingClient(null);
    }
  };

  const getStatusColor = (status: string | null) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  if (clients.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No clients found. Try adjusting your search criteria.
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Client Name</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Industry</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Jobs</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clients.map((client) => (
              <TableRow
                key={client.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => onClientClick(client.id)}
              >
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {client.name}
                    {client.website && (
                      <Globe className="h-3 w-3 text-muted-foreground" />
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    {client.contact_email && (
                      <div className="text-sm">{client.contact_email}</div>
                    )}
                    {client.contact_phone && (
                      <div className="text-xs text-muted-foreground">{client.contact_phone}</div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {client.industry && (
                    <Badge variant="outline" className="text-xs">
                      {client.industry}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {client.address && (
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      <span className="truncate max-w-[150px]" title={client.address}>
                        {client.address}
                      </span>
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {client.jobs?.length || 0}
                    </Badge>
                    {client.jobs && client.jobs.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onClientClick(client.id);
                        }}
                        className="h-6 w-6 p-0"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {client.status && (
                    <Badge className={`text-xs ${getStatusColor(client.status)}`}>
                      {client.status}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {format(new Date(client.created_at), 'MMM d, yyyy')}
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => handleDeleteClick(client, e)}
                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!deletingClient} onOpenChange={() => setDeletingClient(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingClient?.name}"? This action cannot be undone
              and will also remove all associated jobs, contacts, and interactions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? 'Deleting...' : 'Delete Client'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
